import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

export default function GallerySection({ images = [], title = "Billedgalleri" }) {
  const displayImages = images && images.length > 0 ? images : [];

  const [activeIndex, setActiveIndex] = useState(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Handle keyboard events for the lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeIndex === null) return;
      if (e.key === 'Escape') setActiveIndex(null);
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex]);

  if (!displayImages || displayImages.length === 0) return null;

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  // Touch swipe handling for mobile
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance to register as a swipe

    if (swipeDistance > minSwipeDistance) {
      // Swiped left -> next image
      handleNext();
    } else if (swipeDistance < -minSwipeDistance) {
      // Swiped right -> previous image
      handlePrev();
    }
  };

  return (
    <section className="py-12 border-t border-stone-900">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-swamp-300">
              Dokumentation & Erfaring
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white uppercase tracking-tight mt-1">
              {title}
            </h2>
          </div>
          <p className="text-sm text-stone-400 max-w-md">
            Klik på et billede for at se det i fuld størrelse. Du kan navigere med piletasterne på computeren eller swipe på din mobil.
          </p>
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayImages.map((src, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className="group relative aspect-4/3 overflow-hidden rounded-xl bg-stone-900 border border-stone-800/60 hover:border-emerald-800/80 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
            >
              <img
                src={src.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/')}
                alt={`${title} - Billede ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-stone-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="bg-swamp-900 border border-swamp-700 p-3 rounded-full text-swamp-300 transform scale-90 group-hover:scale-100 transition-transform duration-300 shadow-xl font-bold">
                  <ZoomIn className="w-5 h-5 stroke-[2.5]" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {activeIndex !== null && (
        <div 
          className="fixed inset-0 z-50 bg-stone-950/98 flex flex-col justify-between select-none animate-in fade-in duration-200"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Lightbox Header */}
          <div className="p-4 flex items-center justify-between text-white bg-gradient-to-b from-black/60 to-transparent">
            <div className="text-sm font-semibold tracking-wide">
              {title} <span className="text-stone-400 ml-2">({activeIndex + 1} af {displayImages.length})</span>
            </div>
            <button
              onClick={() => setActiveIndex(null)}
              className="p-2 rounded-lg bg-stone-900/80 border border-stone-800 text-stone-300 hover:text-white hover:bg-stone-800 transition-colors cursor-pointer"
              aria-label="Luk visning"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Lightbox Main Content */}
          <div className="flex-grow flex items-center justify-center p-4 relative">
            {/* Previous Button */}
            {displayImages.length > 1 && (
              <button
                onClick={handlePrev}
                className="absolute left-4 md:left-8 z-10 p-3 rounded-full bg-stone-900/80 border border-stone-800 text-stone-300 hover:text-white hover:bg-stone-800 transition-all hover:scale-105 cursor-pointer hidden sm:flex"
                aria-label="Forrige billede"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
            )}

            {/* Active Image */}
            <div className="max-w-full max-h-[80vh] flex items-center justify-center relative">
              <img
                src={displayImages[activeIndex].replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/')}
                alt={`${title} - Stor visning ${activeIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border border-stone-800 animate-in zoom-in-95 duration-200"
              />
            </div>

            {/* Next Button */}
            {displayImages.length > 1 && (
              <button
                onClick={handleNext}
                className="absolute right-4 md:right-8 z-10 p-3 rounded-full bg-stone-900/80 border border-stone-800 text-stone-300 hover:text-white hover:bg-stone-800 transition-all hover:scale-105 cursor-pointer hidden sm:flex"
                aria-label="Næste billede"
              >
                <ChevronRight className="w-7 h-7" />
              </button>
            )}
          </div>

          {/* Lightbox Footer (Mobile Navigation Helper) */}
          <div className="p-6 text-center text-xs text-stone-500 bg-gradient-to-t from-black/60 to-transparent flex flex-col items-center gap-3">
            {images.length > 1 && (
              <div className="flex gap-4 sm:hidden">
                <button
                  onClick={handlePrev}
                  className="px-4 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-300 font-semibold"
                >
                  Forrige
                </button>
                <button
                  onClick={handleNext}
                  className="px-4 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-300 font-semibold"
                >
                  Næste
                </button>
              </div>
            )}
            <p className="hidden sm:block">Brug piletasterne <kbd className="bg-stone-900 px-1.5 py-0.5 rounded border border-stone-800">←</kbd> og <kbd className="bg-stone-900 px-1.5 py-0.5 rounded border border-stone-800">→</kbd> til at skifte billede, eller <kbd className="bg-stone-900 px-1.5 py-0.5 rounded border border-stone-800">ESC</kbd> for at lukke.</p>
            <p className="sm:hidden">Swipe til højre eller venstre for at skifte billede.</p>
          </div>
        </div>
      )}
    </section>
  );
}
